import TreeItem from '@/storybook/components/FormCollection/TreeSelect/Components/TreeItem/TreeItem';
import DropdownDivider from '@/storybook/components/FormCollection/components/DropdownDivider/DropdownDivider';
import DropdownItemLabelWithRatingStars from '@/storybook/components/FormCollection/components/DropdownItemLabelWithRatingsStars/DropdownItemLabelWithRatingsStars';
import ListHeader from '@/storybook/components/FormCollection/components/ListHeader/ListHeader';
import { useFloatingLabelVirtualizedOptions } from '@/storybook/components/FormCollection/hooks/useFloatingLabelVirtualizedOptions';
import { VirtualRowMeta } from '@/storybook/components/FormCollection/hooks/useVirtualizedOptions';
import {
  createSelectItemKey,
  extractSelectItemLabelText,
  getSelectItemLabelContent,
} from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import { useCallback } from 'react';
import styles from '../../MultiLevelSelectFloatingLabel.module.css';

type UseMultiLevelSelectVirtualizationProps<T> = {
  selectedOptions?: SelectItem<T>[];
  notSelectedOptions?: SelectItem<T>[];
  categoriesToDisplay: { category: string; index: number }[];
  noCategoryLabel: string;
  handleClickOnCheckbox: (option: SelectItem<T>) => void;
  isSelected: (option: SelectItem<T>) => boolean;
  value: SelectItem<T>[];
  setSelectedOption: (option: SelectItem<T> | undefined) => void;
};

type VirtualOptionRowProps<T> = {
  option: SelectItem<T>;
  isSelectedRow: boolean;
  isChecked: boolean;
  value: SelectItem<T>[];
  onCheckboxChange: (option: SelectItem<T>) => void;
  onEdit: (option: SelectItem<T> | undefined) => void;
};

const VirtualOptionRow = <T,>({
  option,
  isSelectedRow,
  isChecked,
  value,
  onCheckboxChange,
  onEdit,
}: VirtualOptionRowProps<T>) => {
  const optionKey = createSelectItemKey(option);
  const primaryLabel = extractSelectItemLabelText(option);

  const labelContent = isSelectedRow ? (
    <DropdownItemLabelWithRatingStars option={option} value={value} onEdit={onEdit} />
  ) : (
    getSelectItemLabelContent(option)
  );
  const rowPrefix = isSelectedRow ? 'sortedSelectedOption' : 'sortedNotSelectedOption';
  const checkboxTestId = `${rowPrefix}-${optionKey}`;

  return (
    <TreeItem
      className={!isSelectedRow ? styles.noSelect : undefined}
      label={primaryLabel}
      labelContent={labelContent}
      tooltipText={primaryLabel}
      variant="flatList"
      level={0}
      showCheckbox
      checked={isChecked}
      onCheck={() => onCheckboxChange(option)}
      isSelected={isChecked}
      data-testid={`${rowPrefix}Row-${optionKey}`}
      checkboxDataTestId={checkboxTestId}
    />
  );
};

export const useMultiLevelSelectVirtualizationFloatingLabel = <T,>({
  selectedOptions,
  notSelectedOptions,
  categoriesToDisplay,
  noCategoryLabel,
  handleClickOnCheckbox,
  isSelected,
  value,
  setSelectedOption,
}: UseMultiLevelSelectVirtualizationProps<T>) => {
  const { virtualizedList, parentRef } = useFloatingLabelVirtualizedOptions<T>({
    selectedOptions,
    notSelectedOptions,
    categoriesToDisplay,
    noCategoryLabel,
  });

  const renderVirtualRow = useCallback(
    (vMeta: VirtualRowMeta<T>, virtualIndex?: number) => {
      if (vMeta.kind === 'divider') {
        return <DropdownDivider key={`divider-${virtualIndex}`} />;
      }
      if (vMeta.kind === 'category' && vMeta.category) {
        return (
          <ListHeader
            key={`category-${vMeta.category}`}
            category={vMeta.category}
            isNoCategory={vMeta.isNoCategory}
            hideDivider={vMeta.isFirstCategory}
          />
        );
      }
      if ((vMeta.kind === 'selected' || vMeta.kind === 'notSelected') && vMeta.option) {
        return (
          <VirtualOptionRow
            option={vMeta.option}
            isSelectedRow={vMeta.kind === 'selected'}
            isChecked={isSelected(vMeta.option)}
            value={value}
            onCheckboxChange={handleClickOnCheckbox}
            onEdit={setSelectedOption}
          />
        );
      }
      return null;
    },
    [handleClickOnCheckbox, isSelected, value, setSelectedOption],
  );

  return {
    virtualizedList,
    parentRef,
    renderVirtualRow,
  };
};
